// @ts-expect-error — sqlite-vec-wasm doesn't ship .d.ts; we type our own surface.
import sqlite3InitModule from "sqlite-vec-wasm/dist/sqlite3-bundler-friendly.mjs";

import type { LegRow } from "./types";

interface Sqlite3Module {
  oo1: {
    DB: new () => SqliteDb;
  };
  wasm: {
    allocFromTypedArray(arr: Uint8Array): number;
  };
  capi: {
    sqlite3_deserialize(
      db: number,
      schema: string,
      data: number,
      size: number,
      capacity: number,
      flags: number,
    ): number;
    SQLITE_DESERIALIZE_FREEONCLOSE: number;
    SQLITE_DESERIALIZE_RESIZEABLE: number;
  };
}

interface SqliteDb {
  pointer: number;
  exec<T = unknown>(opts: {
    sql: string;
    bind?: unknown[];
    rowMode?: "array" | "object";
    returnValue?: "resultRows";
  }): T[];
  close(): void;
}

const DB_URL = "/data/index.db";

let dbPromise: Promise<SqliteDb> | null = null;

/** Resolve to a ready-to-query DB, opening + deserializing once and caching the result. */
export function getDb(): Promise<SqliteDb> {
  if (!dbPromise) dbPromise = init();
  return dbPromise;
}

async function init(): Promise<SqliteDb> {
  const sqlite3 = (await sqlite3InitModule()) as Sqlite3Module;

  const res = await fetch(DB_URL);
  if (!res.ok) throw new Error(`failed to fetch ${DB_URL}: ${res.status}`);
  const bytes = new Uint8Array(await res.arrayBuffer());

  const db = new sqlite3.oo1.DB();
  const ptr = sqlite3.wasm.allocFromTypedArray(bytes);
  const flags =
    sqlite3.capi.SQLITE_DESERIALIZE_FREEONCLOSE | sqlite3.capi.SQLITE_DESERIALIZE_RESIZEABLE;
  const rc = sqlite3.capi.sqlite3_deserialize(
    db.pointer,
    "main",
    ptr,
    bytes.byteLength,
    bytes.byteLength,
    flags,
  );
  if (rc !== 0) throw new Error(`sqlite3_deserialize failed (rc=${rc})`);

  return db;
}

/** Encode a 384-d Float32Array as the JSON string sqlite-vec accepts as a vec literal. */
function vectorAsJSON(vec: Float32Array): string {
  // Manual loop avoids the engine boxing each Number through Array.prototype.join.
  let s = "[";
  for (let i = 0; i < vec.length; i++) {
    if (i > 0) s += ",";
    s += vec[i];
  }
  s += "]";
  return s;
}

/** Top-K nearest documents by cosine distance. */
export async function vectorTopK(query: Float32Array, topK: number): Promise<LegRow[]> {
  const db = await getDb();
  const rows = db.exec<[number, number]>({
    sql: "SELECT rowid, vec_distance_cosine(embedding, ?) FROM docs_vec ORDER BY 2 LIMIT ?",
    bind: [vectorAsJSON(query), topK],
    rowMode: "array",
    returnValue: "resultRows",
  });
  return rows;
}

/** Top-K BM25 matches via FTS5; empty queries (after sanitization) return []. */
export async function ftsTopK(query: string, topK: number): Promise<LegRow[]> {
  const sanitized = query
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
  if (!sanitized) return [];

  const db = await getDb();
  try {
    const rows = db.exec<[number, number]>({
      sql: "SELECT rowid, bm25(docs_fts) FROM docs_fts WHERE docs_fts MATCH ? ORDER BY 2 LIMIT ?",
      bind: [sanitized, topK],
      rowMode: "array",
      returnValue: "resultRows",
    });
    return rows;
  } catch {
    // FTS5 MATCH parser is picky; treat parse errors as "no FTS hits."
    return [];
  }
}

/** Fetch document fields for a set of rowids, in any order. */
export async function hydrate(
  ids: readonly number[],
): Promise<
  { id: number; episode: string; kind: "chunk" | "summary"; section: string | null; text: string }[]
> {
  if (ids.length === 0) return [];
  const db = await getDb();
  const placeholders = ids.map(() => "?").join(",");
  const rows = db.exec<{
    id: number;
    episode: string;
    kind: "chunk" | "summary";
    section: string | null;
    text: string;
  }>({
    sql: `SELECT id, episode, kind, section, text FROM docs WHERE id IN (${placeholders})`,
    bind: [...ids],
    rowMode: "object",
    returnValue: "resultRows",
  });
  return rows;
}
